<div class="xx-form-logic p-3">

    <div class="flex align-items-center justify-content-between mb-3">
        <div>
            <h2 class="m-0">Formular-Logik</h2>
            <small class="text-color-secondary">
                Konfigurierte Regeln steuern Felder eines generischen Typs — ohne Java.
                Jedes Szenario ist ein eigener Typ, damit sich die Regeln nicht gegenseitig überlagern.
            </small>
        </div>
        <p-button label="Seed erneut ausführen" icon="fa-light fa-arrows-rotate"
                  severity="secondary" [outlined]="true" [disabled]="busy"
                  (onClick)="runSeed()"></p-button>
    </div>

    @if (busy) {
        <div class="flex align-items-center gap-2 mb-3">
            <p-progressSpinner styleClass="w-2rem h-2rem"></p-progressSpinner>
            <span class="text-color-secondary">Wird geladen …</span>
        </div>
    }

    @if (errorMessage) {
        <p-message severity="error" [text]="errorMessage" styleClass="w-full mb-3"></p-message>
    }

    @if (seedMessage) {
        <p-message severity="success" [text]="seedMessage" styleClass="w-full mb-3"></p-message>
    }

    @if (needsSeed) {
        <p-message severity="warn" styleClass="w-full mb-3"
                   text="Es ist noch kein Szenario angelegt. Über 'Seed erneut ausführen' wird der
                         komplette Katalog idempotent auf diesem Mandanten installiert."></p-message>
    }

    @if (initialized && scenarios.length > 0) {
        <div class="grid">

            <!-- ── left: the scenario catalogue ─────────────────────────────────────── -->
            <div class="col-12 md:col-4 lg:col-3">
                <div class="flex flex-column gap-2">
                    @for (scenario of scenarios; track scenario.key) {
                        <div class="xx-fl-card p-2 border-round cursor-pointer"
                             [class.xx-fl-card--active]="selected?.key === scenario.key"
                             [class.xx-fl-card--disabled]="!scenario.seeded"
                             (click)="selectScenario(scenario)">
                            <div class="flex align-items-center justify-content-between gap-2">
                                <span class="font-medium text-sm">
                                    {{ scenario.key }} · {{ scenario.title }}
                                </span>
                                <p-tag [value]="modeLabel(scenario)"
                                       [severity]="modeSeverity(scenario)"
                                       styleClass="text-xs"></p-tag>
                            </div>
                            <small class="text-color-secondary">
                                {{ scenario.ruleCount }} Regel(n)
                                @if (!scenario.seeded) {
                                    · nicht angelegt
                                }
                            </small>
                        </div>
                    }
                </div>
            </div>

            <!-- ── right: the selected scenario ─────────────────────────────────────── -->
            <div class="col-12 md:col-8 lg:col-9">
                @if (selected) {

                    <div class="mb-3">
                        <h3 class="mt-0 mb-1">{{ selected.key }} · {{ selected.title }}</h3>
                        <p class="mt-0 mb-2 line-height-3">{{ selected.description }}</p>
                        <small class="text-color-secondary">
                            Typ: <code>{{ selected.typeName }}</code> (Id {{ selected.typeId }}) ·
                            Modus: <strong>{{ modeLabel(selected) }}</strong>
                        </small>
                    </div>

                    <!--
                      The rules behind the scenario. Shown BEFORE the form on purpose: the point of the
                      page is to read the rule and then watch it act, not to guess from the outcome.
                      The script is the RESOLVED one (gen_<id> already substituted), i.e. exactly what
                      runs. "Logik öffnen" renders the registered lg.Logic object component.
                    -->
                    @if (selected.rules?.length) {
                        <div class="xx-fl-rules mb-3 border-round">
                            <div class="p-2 font-medium border-bottom-1 surface-border">
                                Regeln dieses Szenarios ({{ selected.rules.length }})
                            </div>
                            @for (rule of selected.rules; track rule.key) {
                                <div class="xx-fl-rule p-2 border-bottom-1 surface-border">
                                    <div class="flex align-items-center flex-wrap gap-2">
                                        <p-tag [value]="rule.hook" severity="info" styleClass="text-xs"></p-tag>
                                        <p-tag [value]="rule.scope" severity="secondary" styleClass="text-xs"></p-tag>
                                        <p-tag [value]="rule.language" severity="contrast" styleClass="text-xs"></p-tag>
                                        <span class="text-sm font-medium">{{ ruleTarget(rule) }}</span>
                                        <span class="text-xs text-color-secondary">
                                            Reihenfolge {{ rule.orderPriority }}
                                            @if (rule.triggerFields?.length) {
                                                · Auslöser: {{ rule.triggerFields.join(', ') }}
                                            }
                                        </span>
                                        <span class="flex-auto"></span>
                                        <p-button size="small" severity="secondary" [text]="true"
                                                  [label]="openedRule?.key === rule.key ? 'Logik schließen' : 'Logik öffnen'"
                                                  [disabled]="rule.logicId === null"
                                                  (onClick)="toggleRule(rule)"></p-button>
                                    </div>

                                    @if (rule.script) {
                                        <pre class="xx-fl-script mt-2 mb-0 p-2 text-xs border-round">{{ rule.script }}</pre>
                                    }

                                    <!--
                                      The real lg.Logic object component — the same one the lg config
                                      pages use, resolved through the entity registry.
                                    -->
                                    @if (openedRule?.key === rule.key && rule.logicId !== null) {
                                        <div class="xx-fl-obc mt-2 p-2 border-round">
                                            <small class="block mb-2 text-color-secondary">
                                                lg.Logic · <code>{{ rule.logicAlias }}</code> (Id {{ rule.logicId }})
                                            </small>
                                            <mvs-object [objectType]="'lg.Logic'"
                                                        [objectId]="rule.logicId"></mvs-object>
                                        </div>
                                    }
                                </div>
                            }
                        </div>
                    }

                    <!-- instance family switch: with vs. without a Java *Form class -->
                    <div class="mb-2 flex flex-wrap gap-2">
                        @for (family of families; track family.objectType) {
                            <p-button [label]="family.label"
                                      size="small"
                                      [outlined]="activeFamily.objectType !== family.objectType"
                                      severity="secondary"
                                      (onClick)="selectFamily(family)"></p-button>
                        }
                    </div>
                    <small class="block mb-3 text-color-secondary">{{ activeFamily.hint }}</small>

                    <!--
                      The real create form. Nothing here renders fields — every effect on screen
                      (ausgeblendet / Pflicht / schreibgeschützt / vorbelegt / gefilterte Werteliste)
                      is the framework reacting to the seeded configuration.

                      The single-element @for is what forces a FRESH form instance whenever the
                      scenario or the family changes: the track key changes, so Angular destroys
                      and rebuilds instead of re-using. Re-using would carry the previous
                      scenario's field states over, and a scenario that only REMOVES a restriction
                      would then appear not to work.
                    -->
                    @if (createDto) {
                        <div class="xx-fl-form border-round p-3">
                            @for (instanceKey of [formInstanceKey]; track instanceKey) {
                                <mvs-crud-object
                                    [objectType]="activeFamily.objectType"
                                    [defaultCreateDto]="createDto">
                                </mvs-crud-object>
                            }
                        </div>
                    }

                } @else {
                    <p-message severity="info" styleClass="w-full"
                               text="Links ein Szenario auswählen."></p-message>
                }
            </div>
        </div>
    }
</div>
